// Reading the files out of a 7-zip archive, in the browser (Squeegee's full backup, a .sqg file, is
// one; docs/import-jev/squeegee/README.md). Only what 7-zip writes for a plain archive is read: the
// header (packed or not), and folders of ONE coder: LZMA2 (Squeegee's files), LZMA (Squeegee's packed
// header), or stored as it is. Anything else (filters, a password) is a SevenZipError, which the import
// screen shows as "couldn't open it".
// The unpacking itself is xz-decompress (MIT; xz-embedded, public domain), which reads .xz streams: each
// packed stream is wrapped as one (xzStream below) and handed to `unxz`, passed in so this file stays
// standalone and the reader is only downloaded when a backup is chosen. Every file read out is checked
// against the CRC the archive keeps for it.
// A damaged or hostile file must end in the plain "couldn't open" message, never a frozen or crashed page
// (Astra, 2 Oct): every count is checked before a list is made or a loop runs, and unpacking stops as soon
// as it gives more than the archive said, or more than 64 MB in all.

export class SevenZipError extends Error {}

// The unpacked bytes of one whole .xz stream, as they come (read here, and stopped when too many).
export type Unxz = (xz: Uint8Array) => ReadableStream<Uint8Array>

export interface SevenZipFile { name: string; bytes: Uint8Array }

const MAGIC = [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c]
// Nothing in a customer backup comes near these; a damaged size or count must not take the phone's memory.
const MAX_UNPACKED = 64 * 1024 * 1024
const MAX_COUNT = 9_999

const K = { end: 0x00, header: 0x01, archiveProps: 0x02, additional: 0x03, main: 0x04, files: 0x05, packInfo: 0x06, unpackInfo: 0x07, substreams: 0x08, size: 0x09, crc: 0x0a, folder: 0x0b, unpackSize: 0x0c, numUnpack: 0x0d, emptyStream: 0x0e, name: 0x11, encodedHeader: 0x17 }

export function isSevenZip(bytes: Uint8Array): boolean {
  return bytes.length >= 32 && MAGIC.every((b, i) => bytes[i] === b)
}

let crcTable: Uint32Array | null = null
export function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      crcTable[n] = c >>> 0
    }
  }
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

class Reader {
  pos = 0
  constructor(readonly b: Uint8Array) {}
  byte(): number {
    if (this.pos >= this.b.length) throw new SevenZipError('the archive ends too soon')
    return this.b[this.pos++]
  }
  bytes(n: number): Uint8Array {
    if (n < 0 || this.pos + n > this.b.length) throw new SevenZipError('the archive ends too soon')
    const out = this.b.subarray(this.pos, this.pos + n)
    this.pos += n
    return out
  }
  uint32(): number {
    const x = this.bytes(4)
    return (x[0] | (x[1] << 8) | (x[2] << 16) | (x[3] << 24)) >>> 0
  }
  uint64(): number {
    const lo = this.uint32()
    const hi = this.uint32()
    if (hi > 0x1fffff) throw new SevenZipError('a size is too big')
    return hi * 2 ** 32 + lo
  }
  // How many of something follow: a whole number under MAX_COUNT that the bytes left could hold, at
  // `bits` bits each at the very least (a bit vector is the tightest anything is written).
  count(bits: number): number {
    const n = this.number()
    if (!Number.isSafeInteger(n) || n > MAX_COUNT || n * bits > (this.b.length - this.pos) * 8) throw new SevenZipError('a count in the archive is impossible')
    return n
  }
  // A size in bytes, at most `max`.
  size(max: number): number {
    const n = this.number()
    if (!Number.isSafeInteger(n) || n > max) throw new SevenZipError('a size in the archive is impossible')
    return n
  }
  // 7-zip's own number: the first byte's top bits say how many more bytes follow.
  number(): number {
    const first = this.byte()
    let mask = 0x80
    let value = 0
    for (let i = 0; i < 8; i++) {
      if ((first & mask) === 0) return value + (first & (mask - 1)) * 2 ** (8 * i)
      value += this.byte() * 2 ** (8 * i)
      mask >>>= 1
    }
    return value
  }
  bits(n: number): boolean[] {
    const out: boolean[] = []
    let cur = 0
    for (let i = 0; i < n; i++) {
      if (i % 8 === 0) cur = this.byte()
      out.push((cur & (0x80 >>> i % 8)) !== 0)
    }
    return out
  }
  // "All defined" byte, or a bit for each, then a CRC for each one defined.
  digests(n: number): (number | null)[] {
    const all = this.byte()
    const defined = all ? new Array<boolean>(n).fill(true) : this.bits(n)
    return defined.map((d) => (d ? this.uint32() : null))
  }
}

interface Coder { id: string; props: Uint8Array }
interface Folder { coder: Coder; unpackSize: number; crc: number | null }
interface Streams { packPos: number; packSizes: number[]; folders: Folder[]; perFolder: number[]; subSizes: number[]; subCrcs: (number | null)[] }

function readFolder(r: Reader): Coder {
  const n = r.number()
  if (n !== 1) throw new SevenZipError('this archive uses more than one coder')
  const flag = r.byte()
  if (flag & 0x80) throw new SevenZipError('this archive uses alternative coders')
  const id = [...r.bytes(flag & 0x0f)].map((x) => x.toString(16).padStart(2, '0')).join('')
  if (flag & 0x10 && (r.number() !== 1 || r.number() !== 1)) throw new SevenZipError('this archive uses a coder with several streams')
  const props = flag & 0x20 ? r.bytes(r.size(64)) : new Uint8Array(0)
  return { id, props }
}

// `fileSize`: the whole archive's length; nothing packed can be longer.
function readStreams(r: Reader, fileSize: number): Streams {
  const s: Streams = { packPos: 0, packSizes: [], folders: [], perFolder: [], subSizes: [], subCrcs: [] }
  for (let t = r.byte(); t !== K.end; t = r.byte()) {
    if (t === K.packInfo) {
      s.packPos = r.size(fileSize)
      // Each packed stream's size takes a byte at least.
      const n = r.count(8)
      for (let p = r.byte(); p !== K.end; p = r.byte()) {
        if (p === K.size) s.packSizes = Array.from({ length: n }, () => r.size(fileSize))
        else if (p === K.crc) r.digests(n)
        else throw new SevenZipError('unexpected pack information')
      }
    } else if (t === K.unpackInfo) {
      if (r.byte() !== K.folder) throw new SevenZipError('no folders')
      // Each folder takes 3 bytes at least (a coder count, a flag, an id), and a size after.
      const n = r.count(32)
      if (r.byte() !== 0) throw new SevenZipError('folders kept elsewhere')
      const coders = Array.from({ length: n }, () => readFolder(r))
      if (r.byte() !== K.unpackSize) throw new SevenZipError('no unpacked sizes')
      const sizes = coders.map(() => r.size(MAX_UNPACKED))
      if (sizes.reduce((a, b) => a + b, 0) > MAX_UNPACKED) throw new SevenZipError('the files in it are too big')
      let crcs: (number | null)[] = coders.map(() => null)
      for (let p = r.byte(); p !== K.end; p = r.byte()) {
        if (p === K.crc) crcs = r.digests(n)
        else throw new SevenZipError('unexpected folder information')
      }
      s.folders = coders.map((coder, i) => ({ coder, unpackSize: sizes[i], crc: crcs[i] }))
      s.perFolder = coders.map(() => 1)
    } else if (t === K.substreams) {
      let p = r.byte()
      if (p === K.numUnpack) {
        s.perFolder = s.folders.map(() => r.size(MAX_COUNT))
        if (s.perFolder.reduce((a, b) => a + b, 0) > MAX_COUNT) throw new SevenZipError('a count in the archive is impossible')
        p = r.byte()
      }
      const sizes: number[] = []
      const hasSizes = p === K.size
      // Every file but each folder's last has its size written, a byte at least.
      if (hasSizes && s.perFolder.reduce((a, n) => a + Math.max(0, n - 1), 0) > r.b.length - r.pos) throw new SevenZipError('a count in the archive is impossible')
      s.folders.forEach((f, i) => {
        const n = s.perFolder[i]
        if (n === 0) return
        let used = 0
        for (let k = 1; k < n; k++) {
          const size = hasSizes ? r.size(MAX_UNPACKED) : 0
          sizes.push(size)
          used += size
        }
        if (used > f.unpackSize) throw new SevenZipError('file sizes do not add up')
        sizes.push(f.unpackSize - used)
      })
      if (hasSizes) p = r.byte()
      s.subSizes = sizes
      // A CRC is listed here for each file whose folder did not already give it.
      const known: (number | null)[] = []
      s.folders.forEach((f, i) => { for (let k = 0; k < s.perFolder[i]; k++) known.push(s.perFolder[i] === 1 && f.crc !== null ? f.crc : null) })
      s.subCrcs = known
      while (p !== K.end) {
        if (p !== K.crc) throw new SevenZipError('unexpected file information')
        const missing = known.filter((c) => c === null).length
        const got = r.digests(missing)
        let j = 0
        s.subCrcs = known.map((c) => (c === null ? got[j++] : c))
        p = r.byte()
      }
    } else throw new SevenZipError('unexpected stream information')
  }
  if (!s.subSizes.length) {
    s.subSizes = s.folders.map((f) => f.unpackSize)
    s.subCrcs = s.folders.map((f) => f.crc)
  }
  return s
}

// All of a stream, stopped (and an error) once it gives more than `most` bytes.
async function readAtMost(stream: ReadableStream<Uint8Array>, most: number): Promise<Uint8Array> {
  const reader = stream.getReader()
  const parts: Uint8Array[] = []
  let total = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      total += value.length
      if (total > most) throw new SevenZipError('the packed data gives more than the archive says')
      parts.push(value)
    }
  } catch (e) {
    void reader.cancel().catch(() => {})
    throw e
  }
  const out = new Uint8Array(total)
  let at = 0
  for (const x of parts) {
    out.set(x, at)
    at += x.length
  }
  return out
}

// Each folder's unpacked bytes, in order. `budget.left`: how much more may be unpacked, all folders together.
async function unpackFolders(all: Uint8Array, s: Streams, unxz: Unxz, budget: { left: number }): Promise<Uint8Array[]> {
  if (s.packSizes.length !== s.folders.length) throw new SevenZipError('packed streams and folders do not match')
  let at = 32 + s.packPos
  const out: Uint8Array[] = []
  for (const [i, f] of s.folders.entries()) {
    const size = s.packSizes[i]
    if (at + size > all.length) throw new SevenZipError('the archive ends too soon')
    if (f.unpackSize > budget.left) throw new SevenZipError('a file in it is too big')
    budget.left -= f.unpackSize
    const packed = all.subarray(at, at + size)
    at += size
    let bytes: Uint8Array
    if (f.coder.id === '00') bytes = packed
    else {
      const xz = f.coder.id === '21' ? xzStream(lzma2Props(f.coder.props), packed, f.unpackSize)
        : f.coder.id === '030101' ? xzStream(...lzmaAsLzma2(f.coder.props, packed, f.unpackSize), f.unpackSize)
        : null
      if (!xz) throw new SevenZipError(`this archive is packed a way Roundly can't read (coder ${f.coder.id})`)
      try {
        bytes = f.unpackSize === 0 ? new Uint8Array(0) : await readAtMost(unxz(xz), f.unpackSize)
      } catch (e) {
        throw new SevenZipError(`the packed data is damaged (${e instanceof Error ? e.message : String(e)})`)
      }
    }
    if (bytes.length !== f.unpackSize) throw new SevenZipError('the packed data is damaged (wrong size)')
    if (f.crc !== null && crc32(bytes) !== f.crc) throw new SevenZipError('the packed data is damaged (CRC)')
    out.push(bytes)
  }
  return out
}

// Every file in the archive with its bytes (empty files and folders as empty bytes).
export async function readSevenZip(all: Uint8Array, unxz: Unxz): Promise<SevenZipFile[]> {
  if (!isSevenZip(all)) throw new SevenZipError('not a 7-zip archive')
  const start = new Reader(all)
  start.pos = 12
  const nextOffset = start.uint64()
  const nextSize = start.uint64()
  const nextCrc = start.uint32()
  if (crc32(all.subarray(12, 32)) !== new Reader(all.subarray(8, 12)).uint32()) throw new SevenZipError('the archive start is damaged')
  const from = 32 + nextOffset
  if (from + nextSize > all.length || nextSize === 0) throw new SevenZipError('the archive ends too soon')
  let header = all.subarray(from, from + nextSize)
  if (crc32(header) !== nextCrc) throw new SevenZipError('the archive header is damaged')
  // A packed header: unpack it first (7-zip's default).
  const budget = { left: MAX_UNPACKED }
  for (let guard = 0; header[0] === K.encodedHeader; guard++) {
    if (guard > 3) throw new SevenZipError('the archive header is damaged')
    const r = new Reader(header)
    r.pos = 1
    header = (await unpackFolders(all, readStreams(r, all.length), unxz, budget))[0] ?? new Uint8Array(0)
  }
  const r = new Reader(header)
  if (r.byte() !== K.header) throw new SevenZipError('no archive header')
  let streams: Streams | null = null
  let names: string[] = []
  let empty: boolean[] = []
  for (let t = r.byte(); t !== K.end; t = r.byte()) {
    if (t === K.archiveProps) {
      for (let p = r.byte(); p !== K.end; p = r.byte()) r.bytes(r.number())
    } else if (t === K.additional) {
      readStreams(r, all.length)
    } else if (t === K.main) {
      streams = readStreams(r, all.length)
    } else if (t === K.files) {
      // Each file takes 2 bytes of name at least (the closing zero).
      const n = r.count(16)
      empty = new Array<boolean>(n).fill(false)
      names = new Array<string>(n).fill('')
      for (let p = r.byte(); p !== K.end; p = r.byte()) {
        const size = r.size(r.b.length - r.pos)
        const data = new Reader(r.bytes(size))
        if (p === K.emptyStream) empty = data.bits(n)
        else if (p === K.name) {
          if (data.byte() !== 0) throw new SevenZipError('file names kept elsewhere')
          const text = new TextDecoder('utf-16le').decode(data.bytes(size - 1))
          names = text.split('\u0000').slice(0, n)
        }
      }
    } else throw new SevenZipError('unexpected archive header')
  }
  const folders = streams ? await unpackFolders(all, streams, unxz, budget) : []
  const files: SevenZipFile[] = []
  // The files with data take the unpacked streams in order, each folder cut into its files.
  const pieces: Uint8Array[] = []
  if (streams) {
    let k = 0
    folders.forEach((bytes, i) => {
      let at = 0
      for (let j = 0; j < streams!.perFolder[i]; j++, k++) {
        const piece = bytes.subarray(at, at + streams!.subSizes[k])
        at += streams!.subSizes[k]
        const want = streams!.subCrcs[k]
        if (want !== null && want !== undefined && crc32(piece) !== want) throw new SevenZipError('a file in it is damaged (CRC)')
        pieces.push(piece)
      }
    })
  }
  let next = 0
  names.forEach((name, i) => {
    if (empty[i]) files.push({ name, bytes: new Uint8Array(0) })
    else {
      if (next >= pieces.length) throw new SevenZipError('files and data do not match')
      files.push({ name, bytes: pieces[next++] })
    }
  })
  return files
}

// ---- wrapping a packed stream as an .xz stream -------------------------------------------------
// The .xz format (tukaani.org/xz/xz-file-format.txt): a stream header, one block holding the LZMA2 data
// with no check of its own (7-zip's CRC is checked after), the index of that one block, a stream footer.

function lzma2Props(props: Uint8Array): number {
  if (props.length !== 1 || props[0] > 40) throw new SevenZipError('LZMA2 settings missing')
  return props[0]
}

// A 7-zip LZMA stream (the packed header) as one LZMA2 chunk: the same packed bytes, framed. One chunk
// holds up to 2 MB unpacked and 64 KB packed; a header is far smaller.
function lzmaAsLzma2(props: Uint8Array, packed: Uint8Array, size: number): [number, Uint8Array] {
  if (props.length !== 5) throw new SevenZipError('LZMA settings missing')
  const lclppb = props[0]
  const lc = lclppb % 9
  const lp = Math.floor(lclppb / 9) % 5
  if (lclppb >= 225 || lc + lp > 4) throw new SevenZipError('LZMA settings Roundly cannot read')
  if (size > 1 << 21 || packed.length > 1 << 16) throw new SevenZipError('this archive is packed a way Roundly can\'t read (large LZMA)')
  const dict = (props[1] | (props[2] << 8) | (props[3] << 16) | (props[4] << 24)) >>> 0
  const need = Math.max(4096, Math.min(dict, size))
  let d = 0
  while (d < 40 && ((2 | (d & 1)) * 2 ** (Math.floor(d / 2) + 11)) < need) d++
  if (size === 0) return [d, new Uint8Array([0])]
  const u = size - 1
  const p = packed.length - 1
  const chunk = new Uint8Array(6 + packed.length + 1)
  chunk.set([0xe0 | (u >>> 16), (u >>> 8) & 0xff, u & 0xff, (p >>> 8) & 0xff, p & 0xff, lclppb])
  chunk.set(packed, 6)
  chunk[chunk.length - 1] = 0
  return [d, chunk]
}

const le32 = (n: number): number[] => [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]
function varint(n: number): number[] {
  const out: number[] = []
  while (n >= 0x80) {
    out.push((n % 0x80) | 0x80)
    n = Math.floor(n / 0x80)
  }
  out.push(n)
  return out
}

export function xzStream(dictProp: number, lzma2: Uint8Array, unpacked: number): Uint8Array {
  const flags = [0, 0]
  const streamHeader = [0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00, ...flags, ...le32(crc32(new Uint8Array(flags)))]
  // Header size 12 (stored as 12 / 4 - 1), no sizes given, one filter: LZMA2 (0x21) with 1 property byte.
  const blockStart = [0x02, 0x00, 0x21, 0x01, dictProp, 0, 0, 0]
  const blockHeader = [...blockStart, ...le32(crc32(new Uint8Array(blockStart)))]
  const pad = (4 - (lzma2.length % 4)) % 4
  const index = [0x00, ...varint(1), ...varint(blockHeader.length + lzma2.length), ...varint(unpacked)]
  while (index.length % 4) index.push(0)
  index.push(...le32(crc32(new Uint8Array(index))))
  const backward = [...le32(index.length / 4 - 1), ...flags]
  const footer = [...le32(crc32(new Uint8Array(backward))), ...backward, 0x59, 0x5a]
  const out = new Uint8Array(streamHeader.length + blockHeader.length + lzma2.length + pad + index.length + footer.length)
  let at = 0
  for (const part of [streamHeader, blockHeader, lzma2, new Array<number>(pad).fill(0), index, footer]) {
    out.set(part, at)
    at += part.length
  }
  return out
}
